/**
 * Read an APK's baked `versionName` / `versionCode` in the browser
 * (ROADMAP APK-UPLOAD-AUTOFILL) so the release-upload dialog can pre-fill
 * the version instead of the superadmin hand-typing it.
 *
 * An APK is a zip; its `AndroidManifest.xml` is Android **binary XML**
 * (AXML), so there's a two-step read: pull the manifest entry out of the
 * zip (inflating via the browser's DecompressionStream), then parse the
 * AXML for the `<manifest>` element's version attributes. No dependencies.
 */

export interface ApkVersion {
  versionName: string | null;
  versionCode: number | null;
}

// ---- AXML parsing --------------------------------------------------------

function readUtf16(dv: DataView, off: number): string {
  let len = dv.getUint16(off, true);
  let p = off + 2;
  if (len & 0x8000) {
    len = ((len & 0x7fff) << 16) | dv.getUint16(p, true);
    p += 2;
  }
  let s = '';
  for (let i = 0; i < len; i++) s += String.fromCharCode(dv.getUint16(p + i * 2, true));
  return s;
}

function readUtf8(dv: DataView, off: number): string {
  let p = off;
  // char count (skipped) then byte count — each 1 or 2 bytes.
  let n = dv.getUint8(p++);
  if (n & 0x80) n = dv.getUint8(p++);
  let byteLen = dv.getUint8(p++);
  if (byteLen & 0x80) byteLen = ((byteLen & 0x7f) << 8) | dv.getUint8(p++);
  const slice = new Uint8Array(dv.buffer, dv.byteOffset + p, byteLen);
  return new TextDecoder('utf-8').decode(slice);
}

function parseStringPool(dv: DataView, chunkOff: number): string[] {
  const stringCount = dv.getUint32(chunkOff + 8, true);
  const flags = dv.getUint32(chunkOff + 16, true);
  const stringsStart = dv.getUint32(chunkOff + 20, true);
  const isUtf8 = (flags & 0x100) !== 0;
  const offsetsBase = chunkOff + 28; // 8-byte chunk header + 5 uint32 fields
  const stringsBase = chunkOff + stringsStart;
  const out: string[] = [];
  for (let i = 0; i < stringCount; i++) {
    const strOff = stringsBase + dv.getUint32(offsetsBase + i * 4, true);
    out.push(isUtf8 ? readUtf8(dv, strOff) : readUtf16(dv, strOff));
  }
  return out;
}

/**
 * Extract `versionName` / `versionCode` from binary AndroidManifest.xml.
 * Exported for the parser unit test (the zip step is browser-only).
 */
export function parseAxmlVersion(bytes: Uint8Array): ApkVersion {
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (bytes.length < 8 || dv.getUint32(0, true) !== 0x00080003) {
    return { versionName: null, versionCode: null };
  }
  let strings: string[] = [];
  let versionName: string | null = null;
  let versionCode: number | null = null;

  let off = 8; // after magic + fileSize
  while (off + 8 <= bytes.length) {
    const type = dv.getUint16(off, true);
    const headerSize = dv.getUint16(off + 2, true);
    const size = dv.getUint32(off + 4, true);
    if (size < 8 || off + size > bytes.length) break;

    if (type === 0x0001) {
      strings = parseStringPool(dv, off);
    } else if (type === 0x0102) {
      // START_ELEMENT — only the <manifest> element carries the versions.
      const body = off + headerSize;
      const elemName = strings[dv.getUint32(body + 4, true)] ?? '';
      if (elemName === 'manifest') {
        const attrStart = dv.getUint16(body + 8, true);
        const attrSize = dv.getUint16(body + 10, true) || 20;
        const attrCount = dv.getUint16(body + 12, true);
        let ap = body + attrStart;
        for (let i = 0; i < attrCount && ap + 20 <= off + size; i++) {
          const aName = strings[dv.getUint32(ap + 4, true)] ?? '';
          const rawValue = dv.getInt32(ap + 8, true);
          const dataType = dv.getUint8(ap + 15);
          const data = dv.getUint32(ap + 16, true);
          if (aName === 'versionName' && versionName === null) {
            versionName =
              dataType === 0x03
                ? strings[data] ?? null
                : rawValue >= 0
                  ? strings[rawValue] ?? null
                  : null;
          } else if (aName === 'versionCode' && versionCode === null) {
            versionCode =
              dataType === 0x10
                ? data
                : rawValue >= 0 && strings[rawValue] != null
                  ? Number(strings[rawValue])
                  : null;
          }
          ap += attrSize;
        }
      }
    }
    off += size;
  }
  return { versionName, versionCode };
}

// ---- zip extraction ------------------------------------------------------

async function inflateRaw(comp: Uint8Array): Promise<Uint8Array> {
  const ds = new DecompressionStream('deflate-raw');
  const writer = ds.writable.getWriter();
  // Copy into a fresh ArrayBuffer-backed array (satisfies BufferSource typing).
  void writer.write(new Uint8Array(comp));
  void writer.close();
  const reader = ds.readable.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value as Uint8Array);
    total += (value as Uint8Array).length;
  }
  const out = new Uint8Array(total);
  let o = 0;
  for (const c of chunks) {
    out.set(c, o);
    o += c.length;
  }
  return out;
}

export async function extractAndroidManifest(apk: ArrayBuffer): Promise<Uint8Array | null> {
  const bytes = new Uint8Array(apk);
  const dv = new DataView(apk);
  // End Of Central Directory (0x06054b50), scanning from the end (comment
  // can be up to 65535 bytes).
  const min = Math.max(0, bytes.length - 65557);
  let eocd = -1;
  for (let i = bytes.length - 22; i >= min; i--) {
    if (dv.getUint32(i, true) === 0x06054b50) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) return null;
  const cdCount = dv.getUint16(eocd + 10, true);
  let p = dv.getUint32(eocd + 16, true);
  const dec = new TextDecoder();
  for (let i = 0; i < cdCount; i++) {
    if (p + 46 > bytes.length || dv.getUint32(p, true) !== 0x02014b50) break;
    const method = dv.getUint16(p + 10, true);
    const compSize = dv.getUint32(p + 20, true);
    const nameLen = dv.getUint16(p + 28, true);
    const extraLen = dv.getUint16(p + 30, true);
    const commentLen = dv.getUint16(p + 32, true);
    const localOff = dv.getUint32(p + 42, true);
    const name = dec.decode(bytes.subarray(p + 46, p + 46 + nameLen));
    if (name === 'AndroidManifest.xml') {
      const lNameLen = dv.getUint16(localOff + 26, true);
      const lExtraLen = dv.getUint16(localOff + 28, true);
      const dataStart = localOff + 30 + lNameLen + lExtraLen;
      const comp = bytes.subarray(dataStart, dataStart + compSize);
      if (method === 0) return comp;
      if (method === 8) return inflateRaw(comp);
      return null;
    }
    p += 46 + nameLen + extraLen + commentLen;
  }
  return null;
}

/**
 * Read `versionName` / `versionCode` from an APK File. Returns null on any
 * problem (not an APK, unexpected format) so the caller can fall back to a
 * hand-typed value.
 */
export async function readApkVersion(file: File): Promise<ApkVersion | null> {
  try {
    const manifest = await extractAndroidManifest(await file.arrayBuffer());
    if (!manifest) return null;
    return parseAxmlVersion(manifest);
  } catch {
    return null;
  }
}
